import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import * as api from '~/Services/Service';
import { FaAngleUp, FaAngleDown } from 'react-icons/fa';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul'; 
import Header from '~/Components/Header/Header';

const ListaPdv = () => {
  const history = useHistory();
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: 'asc' });
  const [pesquisa, setPesquisa] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      setEmpreendimentos([]);
      try {
        const data = await api.BuscarEmpreendimentosPDV();
        setEmpreendimentos(Array.isArray(data) ? data : []);
        setLoading(false);
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleOrdenacao = coluna => {
    setOrdenacao(prev => ({
      coluna,
      direcao:
        prev.coluna === coluna && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const empreendimentosFiltrados = useMemo(() => {
    if (!pesquisa.trim()) return empreendimentos;

    const termoPesquisa = pesquisa.toLowerCase().trim();

    return empreendimentos.filter(emp => {
      const matchDescricao = emp.descricao
        .toLowerCase()
        .includes(termoPesquisa);
      const matchCodigo = emp.codigo.toString().includes(termoPesquisa);
      return matchDescricao || matchCodigo;
    });
  }, [empreendimentos, pesquisa]);

  const empreendimentosOrdenados = useMemo(() => {
    if (!ordenacao.coluna) return empreendimentosFiltrados;

    return [...empreendimentosFiltrados].sort((a, b) => {
      const valorA = a[ordenacao.coluna];
      const valorB = b[ordenacao.coluna];

      if (typeof valorA === 'string') {
        const comparacao = valorA.localeCompare(valorB, 'pt-BR');
        return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
      }

      if (ordenacao.direcao === 'asc') {
        return valorA - valorB;
      }
      return valorB - valorA;
    });
  }, [empreendimentosFiltrados, ordenacao]);

  const getIconeOrdenacao = coluna => {
    if (ordenacao.coluna !== coluna) return '';
    return ordenacao.direcao === 'asc' ? <FaAngleUp /> : <FaAngleDown />;
  };

  const tableStyles = {
    wrapper: {
      borderRadius: '8px',
      overflow: 'hidden',
      border: '1px solid #dee2e6',
    },
    header: {
      backgroundColor: '#0747a6',
      color: '#fff',
      borderColor: '#0747a6',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'background-color 0.2s',
    },
    headerHover: {
      backgroundColor: '#053580',
    },
  };

  const colunas = [
    { chave: 'codigo', label: 'Código' },
    { chave: 'descricao', label: 'Descrição' },
    { chave: 'tempo', label: 'Tempo' },
    { chave: 'limite', label: 'Limite' },
  ];

  const [colunaHover, setColunaHover] = useState(null);

  return (
    <Container fluid className="px-3 py-3">
      <style>
        {`
          .tabela-pdv tbody tr:hover {
            background-color: #e8f4ff !important;
            cursor: pointer;
          }
          .search-input::placeholder {
            color: #6c757d;
            opacity: 0.8;
          }
          
          @media (max-width: 768px) {
            .tabela-pdv thead {
              display: none;
            }
            .tabela-pdv tbody tr {
              display: block;
              margin-bottom: 1rem;
              border: 1px solid #dee2e6;
              border-radius: 8px;
              overflow: hidden;
            }
            .tabela-pdv tbody td {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0.75rem 1rem;
              border: none;
              border-bottom: 1px solid #eee;
            }
            .tabela-pdv tbody td:last-child {
              border-bottom: none;
            }
            .tabela-pdv tbody td::before {
              content: attr(data-label);
              font-weight: 600;
              color: #0747a6;
              margin-right: 1rem;
            }
          }
        `}
      </style>

      <HeaderAzul
        title="Parâmetros do PDV"
        contemPesquisa={true}
        setPesquisa={setPesquisa}
        pesquisa={pesquisa}
      />
      
      <Row>
        <Col>
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Carregando empreendimentos...</p>
            </div>
          )}
          {error && (
            <Alert variant="danger">
              Erro ao carregar os empreendimentos. Tente novamente mais tarde.
            </Alert>
          )}
          {!loading && !error && empreendimentos.length === 0 && (
            <Alert variant="info">Nenhum empreendimento encontrado.</Alert>
          )}

          {!loading &&
            !error &&
            empreendimentos.length > 0 &&
            empreendimentosOrdenados.length === 0 && (
              <Alert variant="warning">
                Nenhum resultado encontrado para "{pesquisa}".
              </Alert>
            )}

          {!loading && !error && empreendimentosOrdenados.length > 0 && (
            <div style={tableStyles.wrapper}>
              <Table bordered hover responsive className="mb-0 tabela-pdv">
                <thead>
                  <tr>
                    {colunas.map(col => (
                      <th
                        key={col.chave}
                        style={{
                          ...tableStyles.header,
                          ...(colunaHover === col.chave
                            ? tableStyles.headerHover
                            : {}),
                        }}
                        onClick={() => handleOrdenacao(col.chave)}
                        onMouseEnter={() => setColunaHover(col.chave)}
                        onMouseLeave={() => setColunaHover(null)}
                      >
                        {col.label}
                        <span
                          style={{ fontSize: '0.75rem', marginLeft: '4px' }}
                        >
                          {getIconeOrdenacao(col.chave)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {empreendimentosOrdenados.map(emp => (
                    <tr
                      key={emp.codigo}
                      onClick={() =>
                        history.push(
                          `/parametrospdv/parametrosdetalhes/${emp.codigo}`
                        )
                      }
                    >
                      <td data-label="Código">{emp.codigo}</td>
                      <td data-label="Descrição">{emp.descricao}</td>
                      <td data-label="Tempo">{emp.tempo}</td>
                      <td data-label="Limite">{emp.limite}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ListaPdv;
